import React, { useState, useEffect } from 'react';
import axios from 'axios';

function AdminUserManagement({ adminToken, userJWT }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserPlan, setNewUserPlan] = useState('free');
  const [newUserIsAdmin, setNewUserIsAdmin] = useState(false);
  const [createdUser, setCreatedUser] = useState(null);
  
  // Edit User State
  const [editingUserId, setEditingUserId] = useState(null);
  const [editPlan, setEditPlan] = useState('');
  const [editIsAdmin, setEditIsAdmin] = useState(false);
  const [editResetToken, setEditResetToken] = useState(false);
  const [editSuccess, setEditSuccess] = useState(null);

  // Load users on mount
  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.get(
        `/api/admin/users/list?admin_token=${encodeURIComponent(adminToken)}`,
        {
          headers: {
            'Authorization': `Bearer ${userJWT}`
          }
        }
      );
      setUsers(response.data.users || []);
    } catch (err) {
      setError('Failed to load users: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await axios.post(
        '/api/admin/users/create',
        {
          admin_token: adminToken,
          email: newUserEmail,
          plan: newUserPlan,
          isAdmin: newUserIsAdmin
        },
        {
          headers: {
            'Authorization': `Bearer ${userJWT}`
          }
        }
      );

      if (response.data.success) {
        setCreatedUser({
          email: response.data.user.email,
          token: response.data.user.user_token,
          adminToken: response.data.user.admin_token || null,
          isAdmin: response.data.user.admin
        });
        setNewUserEmail('');
        setNewUserPlan('free');
        setNewUserIsAdmin(false);
        // Reload users list
        await loadUsers();
      }
    } catch (err) {
      setError('Failed to create user: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user? All their data will be deleted permanently.')) {
      return;
    }

    setError('');
    setLoading(true);

    try {
      const response = await axios.post(
        '/api/admin/users/delete',
        {
          admin_token: adminToken,
          user_id: userId
        },
        {
          headers: {
            'Authorization': `Bearer ${userJWT}`
          }
        }
      );

      if (response.data.success) {
        // Reload users list
        await loadUsers();
      }
    } catch (err) {
      setError('Failed to delete user: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  const startEditUser = (user) => {
    setEditingUserId(user.id);
    setEditPlan(user.plan);
    setEditIsAdmin(user.admin === 1);
    setEditResetToken(false);
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await axios.post(
        '/api/admin/users/update',
        {
          admin_token: adminToken,
          user_id: editingUserId,
          plan: editPlan,
          isAdmin: editIsAdmin,
          resetToken: editResetToken
        },
        {
          headers: {
            'Authorization': `Bearer ${userJWT}`
          }
        }
      );

      if (response.data.success) {
        setEditSuccess({
          email: response.data.user.email,
          message: editResetToken 
            ? `✅ Updated! New token: ${response.data.user.user_token}` 
            : '✅ Updated successfully!'
        });
        setEditingUserId(null);
        await loadUsers();
      }
    } catch (err) {
      setError('Failed to update user: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="admin-user-management">
      {error && (
        <div className="error-box">
          <p>{error}</p>
          <button onClick={() => setError('')}>Dismiss</button>
        </div>
      )}

      {/* Create User Section */}
      <div className="create-user-section">
        <button
          onClick={() => setShowCreateForm(!showCreateForm)}
          className="btn-primary"
        >
          {showCreateForm ? '➖ Cancel' : '➕ Create New User'}
        </button>

        {showCreateForm && (
          <form onSubmit={handleCreateUser} className="create-user-form">
            <h3>Create New User</h3>
            <div className="form-group">
              <label>Email</label>
              <input
                type="email"
                value={newUserEmail}
                onChange={(e) => setNewUserEmail(e.target.value)}
                placeholder="user@example.com"
                required
              />
            </div>

            <div className="form-group">
              <label>Plan</label>
              <select
                value={newUserPlan}
                onChange={(e) => setNewUserPlan(e.target.value)}
              >
                <option value="postcard">Postcard (€10)</option>
                <option value="start">Start (€39)</option>
                <option value="jga">JGA (€79)</option>
                <option value="jga-plus">JGA+ (€129)</option>
                <option value="family">Family (€199)</option>
                <option value="family-plus">Family+ (€999)</option>
              </select>
            </div>

            <div className="form-group checkbox">
              <label>
                <input
                  type="checkbox"
                  checked={newUserIsAdmin}
                  onChange={(e) => setNewUserIsAdmin(e.target.checked)}
                />
                Create as Admin User
              </label>
            </div>

            <button type="submit" disabled={loading} className="btn-success">
              {loading ? 'Creating...' : 'Create User'}
            </button>
          </form>
        )}
      </div>

      {/* Created User Token Display */}
      {createdUser && (
        <div className="created-user-token">
          <h3>✅ User Created Successfully!</h3>
          <p><strong>Email:</strong> {createdUser.email}</p>
          {createdUser.isAdmin && (
            <>
              <p><strong>Admin Token:</strong></p>
              <div className="token-box">
                <code>{createdUser.adminToken}</code>
                <button
                  onClick={() => navigator.clipboard.writeText(createdUser.adminToken)}
                  className="btn-copy"
                >
                  📋 Copy
                </button>
              </div>
            </>
          )}
          <p><strong>Access Token:</strong></p>
          <div className="token-box">
            <code>{createdUser.token}</code>
            <button
              onClick={() => navigator.clipboard.writeText(createdUser.token)}
              className="btn-copy"
            >
              📋 Copy
            </button>
          </div>
          <p className="note">⚠️ Send this token to the user via email. They will need it to login.</p>
          <button onClick={() => setCreatedUser(null)} className="btn-secondary">
            Done
          </button>
        </div>
      )}

      {/* Users List */}
      <div className="users-list-section">
        <h3>All Users ({users.length})</h3>
        {loading ? (
          <p>Loading users...</p>
        ) : users.length === 0 ? (
          <p>No users yet.</p>
        ) : (
          <div className="users-table">
            <table>
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Plan</th>
                  <th>Role</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.id}>
                    <td>{user.email}</td>
                    <td>{user.plan}</td>
                    <td>
                      <span className={`badge ${user.admin === 1 ? 'admin' : 'user'}`}>
                        {user.admin === 1 ? '👨‍💼 Admin' : '👤 User'}
                      </span>
                    </td>
                    <td>{user.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}</td>
                    <td>
                      <button 
                        onClick={() => startEditUser(user)}
                        className="btn-edit"
                        disabled={loading}
                      >
                        ✏️ Edit
                      </button>
                      <button 
                        onClick={() => handleDeleteUser(user.id)}
                        className="btn-delete"
                        disabled={loading}
                      >
                        🗑️ Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Edit User Modal */}
      {editingUserId && (
        <div className="edit-modal-overlay">
          <div className="edit-modal">
            <h3>Edit User</h3>
            
            {editSuccess && (
              <div className="success-box">
                <p>✅ {editSuccess.message}</p>
                <button onClick={() => setEditSuccess(null)}>Dismiss</button>
              </div>
            )}

            <form onSubmit={handleUpdateUser} className="edit-user-form">
              <div className="form-group">
                <label>Plan</label>
                <select
                  value={editPlan}
                  onChange={(e) => setEditPlan(e.target.value)}
                >
                  <option value="postcard">Postcard (€10)</option>
                  <option value="start">Start (€39)</option>
                  <option value="jga">JGA (€79)</option>
                  <option value="jga-plus">JGA+ (€129)</option>
                  <option value="family">Family (€199)</option>
                  <option value="family-plus">Family+ (€999)</option>
                </select>
              </div>

              <div className="form-group checkbox">
                <label>
                  <input
                    type="checkbox"
                    checked={editIsAdmin}
                    onChange={(e) => setEditIsAdmin(e.target.checked)}
                  />
                  Admin User
                </label>
              </div>

              <div className="form-group checkbox">
                <label>
                  <input
                    type="checkbox"
                    checked={editResetToken}
                    onChange={(e) => setEditResetToken(e.target.checked)}
                  />
                  Reset Access Token (generate new)
                </label>
              </div>

              <div className="modal-buttons">
                <button type="submit" disabled={loading} className="btn-success">
                  {loading ? 'Updating...' : 'Save Changes'}
                </button>
                <button 
                  type="button" 
                  onClick={() => setEditingUserId(null)}
                  className="btn-secondary"
                  disabled={loading}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <style jsx>{`
        .admin-user-management {
          padding: 20px;
          background: rgba(15, 23, 42, 0.5);
          border-radius: 8px;
          color: #fff;
        }

        h2 {
          margin-bottom: 20px;
          color: #fff;
        }

        h3 {
          margin: 15px 0 10px 0;
          color: #e0e7ff;
        }

        .error-box {
          background: rgba(239, 68, 68, 0.1);
          border: 1px solid rgba(239, 68, 68, 0.5);
          color: #fca5a5;
          padding: 15px;
          border-radius: 6px;
          margin-bottom: 20px;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .create-user-section {
          margin: 20px 0;
          padding: 15px;
          background: rgba(30, 41, 59, 0.8);
          border-radius: 6px;
          border: 1px solid rgba(168, 85, 247, 0.3);
        }

        .create-user-form {
          margin-top: 15px;
          padding: 15px;
          background: rgba(15, 23, 42, 0.8);
          border-radius: 6px;
          border-left: 4px solid #a855f7;
        }

        .form-group {
          margin-bottom: 12px;
        }

        .form-group label {
          display: block;
          margin-bottom: 5px;
          font-weight: 600;
          color: #cbd5e1;
        }

        .form-group input,
        .form-group select {
          width: 100%;
          padding: 8px;
          border: 1px solid rgba(168, 85, 247, 0.3);
          border-radius: 4px;
          font-size: 14px;
          background: rgba(30, 41, 59, 0.8);
          color: #fff;
        }

        .form-group.checkbox {
          margin-top: 15px;
          padding: 10px;
          background: rgba(15, 23, 42, 0.8);
          border-radius: 4px;
          border-left: 3px solid #a855f7;
        }

        .form-group.checkbox label {
          display: flex;
          align-items: center;
          margin-bottom: 0;
          cursor: pointer;
          font-weight: 500;
          color: #cbd5e1;
        }

        .form-group.checkbox input[type="checkbox"] {
          width: 18px;
          height: 18px;
          margin-right: 10px;
          cursor: pointer;
          accent-color: #a855f7;
        }

        .btn-primary,
        .btn-success,
        .btn-secondary,
        .btn-copy {
          padding: 10px 15px;
          border: none;
          border-radius: 4px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s;
        }

        .btn-primary {
          background: #a855f7;
          color: white;
        }

        .btn-primary:hover {
          background: #9333ea;
        }

        .btn-success {
          background: #a855f7;
          color: white;
          width: 100%;
        }

        .btn-success:hover {
          background: #9333ea;
        }

        .btn-success:disabled {
          background: rgba(168, 85, 247, 0.4);
          cursor: not-allowed;
        }

        .btn-secondary {
          background: rgba(168, 85, 247, 0.5);
          color: white;
          margin-top: 10px;
        }

        .btn-secondary:hover {
          background: #a855f7;
        }

        .created-user-token {
          background: rgba(34, 197, 94, 0.1);
          border: 1px solid rgba(34, 197, 94, 0.5);
          padding: 15px;
          border-radius: 6px;
          margin: 20px 0;
          border-left: 4px solid #22c55e;
          color: #86efac;
        }

        .token-box {
          background: rgba(15, 23, 42, 0.8);
          border: 1px solid rgba(168, 85, 247, 0.3);
          padding: 12px;
          border-radius: 4px;
          margin: 10px 0;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .token-box code {
          font-family: 'Courier New', monospace;
          font-size: 12px;
          word-break: break-all;
          flex: 1;
          color: #a78bfa;
        }

        .btn-copy {
          background: #a855f7;
          color: white;
          margin-left: 10px;
        }

        .btn-copy:hover {
          background: #9333ea;
        }

        .note {
          font-size: 12px;
          color: #cbd5e1;
          margin: 10px 0;
          font-style: italic;
        }

        .users-list-section {
          margin-top: 30px;
          padding: 15px;
          background: rgba(30, 41, 59, 0.8);
          border-radius: 6px;
          border: 1px solid rgba(168, 85, 247, 0.3);
        }

        .users-table {
          overflow-x: auto;
          margin-top: 15px;
        }

        table {
          width: 100%;
          border-collapse: collapse;
          font-size: 14px;
        }

        table thead {
          background: rgba(15, 23, 42, 0.6);
          border-bottom: 2px solid rgba(168, 85, 247, 0.3);
        }

        table th {
          padding: 12px;
          text-align: left;
          font-weight: 600;
          color: #cbd5e1;
        }

        table td {
          padding: 10px 12px;
          border-bottom: 1px solid rgba(168, 85, 247, 0.1);
          color: #e0e7ff;
        }

        table tbody tr:hover {
          background: rgba(168, 85, 247, 0.1);
        }

        .badge {
          display: inline-block;
          padding: 4px 8px;
          border-radius: 12px;
          font-size: 12px;
          font-weight: 600;
        }

        .badge.admin {
          background: rgba(168, 85, 247, 0.2);
          color: #c084fc;
        }

        .badge.user {
          background: rgba(59, 130, 246, 0.2);
          color: #93c5fd;
        }

        .btn-delete {
          background: #dc3545;
          color: white;
          padding: 6px 12px;
          border: none;
          border-radius: 4px;
          font-size: 13px;
          cursor: pointer;
          transition: all 0.3s;
        }

        .btn-delete:hover {
          background: #c82333;
        }

        .btn-delete:disabled {
          background: rgba(168, 85, 247, 0.3);
          cursor: not-allowed;
        }

        .btn-edit {
          background: #a855f7;
          color: white;
          padding: 6px 12px;
          border: none;
          border-radius: 4px;
          font-size: 13px;
          cursor: pointer;
          transition: all 0.3s;
          margin-right: 5px;
        }

        .btn-edit:hover {
          background: #9333ea;
        }

        .btn-edit:disabled {
          background: rgba(168, 85, 247, 0.3);
          cursor: not-allowed;
        }

        .edit-modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.7);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
        }

        .edit-modal {
          background: rgba(15, 23, 42, 0.95);
          border: 1px solid rgba(168, 85, 247, 0.5);
          border-radius: 8px;
          padding: 25px;
          max-width: 500px;
          width: 90%;
          color: #fff;
        }

        .edit-modal h3 {
          margin-top: 0;
          margin-bottom: 20px;
          color: #fff;
        }

        .edit-user-form .form-group {
          margin-bottom: 15px;
        }

        .modal-buttons {
          display: flex;
          gap: 10px;
          margin-top: 20px;
        }

        .modal-buttons button {
          flex: 1;
          padding: 10px;
          border: none;
          border-radius: 4px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s;
        }

        .success-box {
          background: rgba(34, 197, 94, 0.1);
          border: 1px solid rgba(34, 197, 94, 0.5);
          color: #86efac;
          padding: 12px;
          border-radius: 6px;
          margin-bottom: 15px;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .success-box button {
          background: rgba(34, 197, 94, 0.3);
          color: #86efac;
          border: none;
          padding: 4px 8px;
          border-radius: 3px;
          cursor: pointer;
          font-size: 12px;
        }

        .success-box button:hover {
          background: rgba(34, 197, 94, 0.5);
        }
      `}</style>
    </div>
  );
}

export default AdminUserManagement;
